import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Clock, MapPin, SealCheck, Eye, HandCoins } from "@phosphor-icons/react";
import { MaskedLine, Reveal, Overline } from "../components/Kinetic";
import { EditorialMarquee } from "../components/Marquee";
import { TWO_A_URL, EvalNote } from "../components/EstimateCTA";
import { Seo, ORG_SCHEMA, WEBSITE_SCHEMA } from "../components/Seo";
import { CATEGORIES } from "../data/categories";
import { api, imgSrc } from "../lib/api";

const HERO_IMG = "/api/static/images/hero-iphone.webp";
const CHICAGO_IMG = "https://images.unsplash.com/photo-1714662660476-022bfd34cf44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjaGljYWdvJTIwc2t5bGluZSUyMGRheXxlbnwwfHx8fDE3ODYxMjc0ODB8MA&ixlib=rb-4.1.0&q=85";

const MANIFESTO = [
  { n: "01", title: "Evaluation", body: "Your device is tested in front of you — screen, cameras, charging, battery. The offer follows from what we find, explained line by line." },
  { n: "02", title: "Data Privacy", body: "We walk you through account removal and secure erasure before any device changes hands. Your data leaves with you, never with the device." },
  { n: "03", title: "Fast Payment", body: "Accept the offer and you are paid on the spot. No store credit, no bill credits spread over three years, no waiting for a check." },
];

const BENTO_SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];

export default function Home() {
  const [guides, setGuides] = useState([]);
  const { scrollYProgress } = useScroll();
  const yHero = useTransform(scrollYProgress, [0, 0.25], [0, -70]);
  const yBadge = useTransform(scrollYProgress, [0, 0.25], [0, 50]);

  useEffect(() => {
    api.get("/articles?limit=3").then((r) => setGuides(r.data.articles)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title={null}
        description="Sell your phone, laptop, tablet, watch, or console in Chicago. Working devices only, transparent in-person evaluations, fast payment. Plus expert buyback guides."
        path="/"
        jsonLd={[ORG_SCHEMA, WEBSITE_SCHEMA]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 md:pt-44" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 pb-24 md:px-12 lg:grid-cols-12 lg:pb-32">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
              <Overline>Chicago Electronics Buyback</Overline>
            </motion.div>
            <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter text-black sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.15}>SELL YOUR PHONE</MaskedLine>
              <MaskedLine delay={0.3}>IN <span className="text-brand">CHICAGO.</span></MaskedLine>
            </h1>
            <motion.p
              className="mt-8 max-w-lg text-lg leading-relaxed text-[#4A4A4A]"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            >
              Turn your electronics into cash. Sell eligible phones, laptops, tablets, watches,
              and gaming consoles locally in Chicago — working devices only, fast evaluation, simple process.
            </motion.p>
            <motion.div
              className="mt-10 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid="hero-estimate-btn"
                className="group flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-navy">
                Get a Buyback Estimate
                <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link to="/how-it-works" data-testid="hero-how-it-works-btn"
                className="group flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white">
                How It Works
                <ArrowUpRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <EvalNote className="basis-full pt-1" />
            </motion.div>
            <motion.div
              className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }}
            >
              {[["Working devices only", SealCheck], ["Fast in-person evaluation", Clock], ["Local Chicago business", MapPin]].map(([label, Icon]) => (
                <span key={label} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#4A4A4A]">
                  <Icon size={16} weight="duotone" className="text-brand" /> {label}
                </span>
              ))}
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div
              className="spotlight-frame relative overflow-hidden rounded-[2rem]"
              initial={{ opacity: 0, scale: 0.94, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.img src={imgSrc(HERO_IMG)} alt="An iPhone 17 Pro Max in cosmic orange held in hand, ready for a buyback evaluation in Chicago"
                className="h-[420px] w-full object-cover md:h-[520px]" style={{ y: yHero }} loading="eager" />
            </motion.div>
            <motion.div
              className="spotlight-frame absolute -bottom-8 -left-6 rounded-2xl border border-black/10 bg-white px-6 py-5 md:-left-12"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              style={{ y: yBadge }}
              data-testid="hero-floating-badge"
            >
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#8A8A8A]">
                <HandCoins size={16} weight="duotone" className="text-cash" /> Paid on the spot
              </p>
              <p className="mt-1 font-display text-lg font-bold tracking-tight text-navy">Evaluation in minutes</p>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* CATEGORIES BENTO */}
      <section className="py-24 md:py-32" data-testid="categories-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Overline>What We Buy</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter md:text-5xl">
              Working electronics, in good condition. Nothing broken.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-12">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.06} className={BENTO_SPANS[i]}>
                <Link to={`/${c.slug}`} data-testid={`category-card-${c.slug}`}
                  className="group relative flex h-full min-h-[240px] flex-col justify-end overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:scale-[1.02] hover:border-black/20 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                  <img src={imgSrc(c.image)} alt={c.imageAlt} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-navy/45 transition-colors duration-300 group-hover:bg-navy/35" />
                  <div className="relative flex items-end justify-between p-7">
                    <div>
                      <p className="font-display text-2xl font-bold tracking-tight text-white">{c.title}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{c.models.length}+ model families accepted</p>
                    </div>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-navy transition-all duration-300 group-hover:bg-brand group-hover:text-white">
                      <ArrowUpRight size={18} weight="bold" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WORKING DEVICES ONLY BAND */}
      <section className="bg-navy py-24 md:py-28" data-testid="working-devices-band">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/60">
              <ShieldCheck size={18} weight="duotone" className="text-cash" /> Our one hard rule
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white md:text-6xl">
              We buy working devices.<br /><span className="text-white/50">Not broken ones.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/70">
              Buyback works because your device gets a second life with a new owner. That only happens when it
              powers on, works properly, and is in good condition. Eligibility and value depend on model,
              condition, functionality, and account status — and we will always tell you exactly where your device stands.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NUMBERED MANIFESTO */}
      <section className="py-24 md:py-36" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><Overline>What We Stand For</Overline></Reveal>
          <div className="mt-10 flex flex-col">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.08}>
                <div className="group grid items-start gap-6 border-t border-black/10 py-12 md:grid-cols-12 md:py-16">
                  <span className="outline-number font-display text-7xl font-extrabold leading-none md:col-span-3 md:text-8xl">{m.n}</span>
                  <div className="md:col-span-9">
                    <h3 className="font-display text-3xl font-bold tracking-tight text-black transition-colors duration-300 group-hover:text-brand md:text-4xl">{m.title}</h3>
                    <p className="mt-4 max-w-xl text-base leading-relaxed text-[#4A4A4A] md:text-lg">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST GUIDES */}
      <section className="bg-[#F5F5F7] py-24 md:py-32" data-testid="latest-guides-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><Overline>Buyback Guides</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tighter md:text-5xl">Learn before you sell.</h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link to="/guides" data-testid="view-all-guides-link"
                className="group flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-navy transition-colors hover:text-brand">
                All guides <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {guides.map((g, i) => (
              <Reveal key={g.slug} delay={i * 0.08}>
                <Link to={`/guides/${g.slug}`} data-testid={`home-guide-${g.slug}`}
                  className="group block overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]">
                  <div className="h-48 overflow-hidden">
                    <img src={imgSrc(g.image_url)} alt={g.image_alt} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{g.category}</p>
                    <h3 className="mt-2 font-display text-lg font-bold leading-snug tracking-tight text-black group-hover:text-brand">{g.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-[#4A4A4A]">{g.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CHICAGO BANNER */}
      <section className="relative overflow-hidden" data-testid="chicago-banner">
        <img src={CHICAGO_IMG} alt="The Chicago skyline on a clear day" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative mx-auto max-w-7xl px-6 py-28 md:px-12 md:py-36">
          <Reveal>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-white/60">
              <MapPin size={16} weight="duotone" className="text-cash" /> Proudly local
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter text-white md:text-5xl">
              Built for Chicago sellers, by people who know the local market.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/chicago-buyback-guide" data-testid="chicago-guide-btn"
                className="rounded-full bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-brand hover:text-white">
                Chicago Buyback Guide
              </Link>
              <Link to="/guides/selling-electronics-locally-vs-shipping-online" data-testid="local-vs-shipping-btn"
                className="rounded-full border border-white/40 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-white hover:text-navy">
                Local vs Shipping
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32" data-testid="final-cta">
        <div className="mx-auto max-w-7xl px-6 text-center md:px-12">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter md:text-6xl">
              Your drawer of old devices is a paycheck.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto mt-6 max-w-xl text-lg text-[#4A4A4A]">
              Get an indicative range in under a minute. Final offer after a free, transparent in-person evaluation.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid="final-estimate-btn"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-brand px-10 py-5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-navy">
              Get a Buyback Estimate
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <div className="mt-5 flex justify-center"><EvalNote /></div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
